import { getConsent, onConsentChange } from './consent.js';

// =====================================================================
//  THIRD-PARTY IDs — fill these in, leave blank ('') to keep a tool off.
//  Each tool only loads AFTER the visitor consents to the matching cookie
//  category (analytics / marketing). Nothing here runs until an ID is set.
// =====================================================================
export const INTEGRATIONS = {
  // --- Analytics category (loads on consent.analytics) ---
  gtmId: 'GTM-5K4DRWZD', // Google Tag Manager container (www.accounteditor.com).
                         // GA4 (Measurement ID G-8G08PB71VV) is configured INSIDE
                         // this container via the Google Tag — NOT here.
  ga4Id: '',       // Leave blank when using GTM above (GTM loads GA4). Setting both
                   //                is ignored — gtmId wins (see loadAnalyticsTools).
  clarityId: '',   // 'xxxxxxxxxx'   Microsoft Clarity — heatmaps + session recordings (free).

  // --- Marketing category (loads on consent.marketing) ---
  metaPixelId: '793295659700816', // Meta (Facebook/Instagram) Pixel — retargeting.
  linkedInId: '',  // '1234567'      LinkedIn Insight Tag — B2B retargeting.
  googleAdsId: 'AW-17574792769', // Google Ads tag. Loading this alone already
                                 // earns its keep: gtag writes the click id to a
                                 // first-party _gcl_* cookie (what a GTM
                                 // Conversion Linker tag does), which is what
                                 // lets a later conversion be credited to the ad
                                 // click, and it builds remarketing audiences.
                                 // Reporting a CONVERSION additionally needs a
                                 // per-action label — see GOOGLE_ADS_CONVERSIONS.
};

// Google Ads conversion actions, keyed by our internal event name (the same
// names passed to track() in analytics.js).
//
// Each value is the full `send_to` string for ONE conversion action:
// '<AW-id>/<conversion-label>'. The label is NOT the tag ID above and is not
// guessable — copy it from Google Ads → Goals → Conversions → <your action> →
// "Tag setup" → "Install the tag yourself", where the snippet reads:
//   gtag('event', 'conversion', {'send_to': 'AW-17574792769/AbC-D_efGhIjKlM'})
//
// Leave a value blank and that event simply doesn't report a conversion — the
// rest of the tracking is unaffected, so it is safe to fill these in one at a
// time as you create each action in the Ads UI.
//
// A note on what these two events actually mean, because it changes how you
// should bid on them: the real conversion (a Shopify app install) completes on
// apps.shopify.com, which no tag on this domain can observe. install_intent is
// therefore a PROXY — it counts clicks on an App Store link, not installs, and
// will read higher than your true install count. Treat it as a bidding signal,
// not as install truth. Attributing real installs needs offline conversion
// import (store the raw gclid at click time, upload installs via the Ads API).
export const GOOGLE_ADS_CONVERSIONS = {
  // "Page view" action — Enabled, Secondary, goal "Page views", counts every
  // conversion, 90-day window. Fires on first load AND every SPA route change,
  // so its conversion count tracks total page views, which is what that goal
  // type is for. Secondary, so it does not feed Smart Bidding.
  //
  // Knowingly redundant: the same page views also reach Ads through the
  // GA4-imported "page_view" action from the Account Editor Website property.
  // Ads therefore counts each page view twice, under two separate actions. Both
  // are Secondary so bidding is unaffected, but if you want clean reporting,
  // remove ONE of them — keep this tag one and drop the GA4 import, or blank
  // this value out and keep the import. Don't leave both if the number matters.
  page_view: 'AW-17574792769/jfP5CI2Pi6QbEMGcqLxB',

  // The Ads-tag action matching this event is "Add App Click"
  // (label 4eMrCMmni6QbEMGcqLxB), which is in a REMOVED state — firing it
  // records nothing. Left blank deliberately. Re-enable the action in Ads first,
  // then paste the label in; installs otherwise arrive only via the GA4-imported
  // "install_intent" action.
  install_intent: '',

  // No Ads-tag action exists for this event. It reaches Ads through the
  // GA4-imported "demo_request" action instead, so no label to set.
  demo_request: '',
};

const loaded = new Set();

function injectScript(src, { async = true, attrs = {} } = {}) {
  const s = document.createElement('script');
  s.src = src;
  s.async = async;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
  return s;
}

function loadGtm(id) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
  injectScript(`https://www.googletagmanager.com/gtm.js?id=${id}`);
}

// Define window.gtag + dataLayer without loading anything. Shared by the GA4 and
// Google Ads loaders so whichever runs second reuses the existing queue rather
// than replacing it (which would drop the other tool's queued calls).
function ensureGtag() {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
}

function loadGa4(id) {
  ensureGtag();
  injectScript(`https://www.googletagmanager.com/gtag/js?id=${id}`);
  window.gtag('js', new Date());
  window.gtag('config', id);
}

function loadGoogleAds(id) {
  ensureGtag();
  injectScript(`https://www.googletagmanager.com/gtag/js?id=${id}`);
  window.gtag('js', new Date());
  window.gtag('config', id);
}

function loadClarity(id) {
  (function (c, l, a, r, i) {
    c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
    const t = l.createElement(r);
    t.async = 1;
    t.src = 'https://www.clarity.ms/tag/' + i;
    const y = l.getElementsByTagName(r)[0];
    y.parentNode.insertBefore(t, y);
  })(window, document, 'clarity', 'script', id);
}

function loadMetaPixel(id) {
  /* eslint-disable */
  !function (f, b, e, v, n, t, s) {
    if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
    t = b.createElement(e); t.async = !0; t.src = v;
    s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq('init', id);
  window.fbq('track', 'PageView');
}

function loadLinkedIn(id) {
  window._linkedin_partner_id = id;
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  window._linkedin_data_partner_ids.push(id);
  injectScript('https://snap.licdn.com/li.lms-analytics/insight.min.js');
}

function loadAnalyticsTools() {
  if (loaded.has('analytics')) return;
  loaded.add('analytics');
  if (INTEGRATIONS.gtmId) loadGtm(INTEGRATIONS.gtmId);
  else if (INTEGRATIONS.ga4Id) loadGa4(INTEGRATIONS.ga4Id);
  if (INTEGRATIONS.clarityId) loadClarity(INTEGRATIONS.clarityId);
}

function loadMarketingTools() {
  if (loaded.has('marketing')) return;
  loaded.add('marketing');
  if (INTEGRATIONS.metaPixelId) loadMetaPixel(INTEGRATIONS.metaPixelId);
  if (INTEGRATIONS.linkedInId) loadLinkedIn(INTEGRATIONS.linkedInId);
  if (INTEGRATIONS.googleAdsId) loadGoogleAds(INTEGRATIONS.googleAdsId);
}

// Load whatever the current consent allows. Safe to call repeatedly — each tool
// loads at most once (guarded by the `loaded` set).
export function applyConsentedIntegrations() {
  if (typeof window === 'undefined') return;
  const c = getConsent();
  if (!c) return; // no choice yet → load nothing
  if (c.analytics) loadAnalyticsTools();
  if (c.marketing) loadMarketingTools();
}

// Run once now, then re-run whenever the visitor updates their cookie choices.
export function initIntegrations() {
  applyConsentedIntegrations();
  return onConsentChange(applyConsentedIntegrations);
}
